import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Textarea } from '@americanexpress/dls-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import styles from '../styles/PageWrapper.scss';
import sendButton from '../assets/SendButton.svg';
import {
  IDLE, LOADING, MODULE_NAME, SUCCESS,
} from '../utils/constants';

const placeholder = (apiStatus, intl) => {
  switch (apiStatus) {
    case IDLE:
      return intl.formatMessage({ id: 'userInput.placeholder' });
    case LOADING:
      return '...';
    case SUCCESS:
      return intl.formatMessage({ id: 'userInput.refineList' });
    default:
      return '';
  }
};

const InputComponent = ({
  sendMessage, conversationApiStatus, setCreateModalIsOpen,
}) => {
  const intl = useIntl();
  const selectedHotels = useSelector((state) => state.getIn(['modules', MODULE_NAME, 'selectedHotels'])).toJS();
  const [value, setValue] = useState('');

  const handleUserInput = (e) => {
    e.preventDefault();
    if (value) {
      sendMessage(value);
      setValue('');
    }
  };
  const showCreateButton = selectedHotels?.length > 0 && !value;

  return (
    <div className="pad-2-tb container">
      <motion.div
        layout={true}
        className="flex width-full margin-1-b"
        transition={{ type: 'spring', stiffness: 300, damping: 40 }}
      >
        <motion.form
          layout={true}
          onSubmit={handleUserInput}
          className={styles.inputContainer}
        >
          <Textarea
            id="text-input"
            type="text"
            aria-labelledby="User input for AI wishlist suggestions"
            aria-label="User Input"
            placeholder={placeholder(conversationApiStatus, intl)}
            status="default"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className={styles.inputBorderRadius}
            style={{
              position: 'relative',
              width: '100%',
              flex: 1,
              minWidth: 0,
              paddingRight: value ? '4rem' : '1rem',
              fieldSizing: 'content',
              resize: 'none',
              borderRadius: '.5rem',
              wordBreak: 'break-all',
            }}
          />

          {value && (
            <button
              id="sendButton"
              type="button"
              aria-label="Send user input for AI suggestions"
              className={styles.sendButtonWrapper}
              onClick={handleUserInput}
            >
              <div className={`${styles.sendButton} ${value && styles.sendButtonActive}`}>
                <div className={styles.sendImageWrapper}>
                  <img
                    src={sendButton}
                    className="height-full width-full"
                    alt="Send Button"
                    draggable={false}
                    aria-hidden="true"
                  />
                </div>
              </div>
            </button>
          )}
        </motion.form>

        <AnimatePresence initial={false}>
          {showCreateButton && (
            <motion.div
              key="createButtonShell"
              initial={{ maxWidth: 0, opacity: 0, x: 16 }}
              animate={{ maxWidth: 120, opacity: 1, x: 0 }}
              exit={{ maxWidth: 0, opacity: 0, x: 16 }}
              transition={{ type: 'spring', stiffness: 300, damping: 40 }}
              style={{
                overflow: 'hidden',
                flexShrink: 0,
                display: 'flex',
              }}
            >
              <button
                className={`${styles.createButton} body-3`}
                id="createButton"
                data-testid="createButton"
                type="button"
                aria-label="Create wishlist with selected hotels"
                onClick={() => setCreateModalIsOpen(true)}
                style={{ whiteSpace: 'nowrap' }}
              >
                <div>
                  {intl.formatMessage({ id: 'userInput.createButton' })}
                </div>
                <div className={styles.selectedNumberWrapper}>
                  {selectedHotels.length}
                </div>
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <div
        className={`text-align-center body-xsmall ${styles.disclaimer}`}
        style={{ fontSize: '.625rem' }}
      >
        {intl.formatMessage({ id: 'userInput.disclaimer' })}
      </div>
    </div>
  );
};

export default InputComponent;

InputComponent.propTypes = {
  sendMessage: PropTypes.func.isRequired,
  conversationApiStatus: PropTypes.string.isRequired,
  setCreateModalIsOpen: PropTypes.func.isRequired,
};
